'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, CheckCircle, Close, Upload } from './Icons'

/**
 * Control/Internship.js ka React version.
 *
 * Validation ka order, saare messages, field limits aur dono API calls
 * (/api/upload, /api/internship) bilkul wahi hain jo pehle the — sirf markup
 * naye design system ka hai.
 *
 * Ek badlaav: pehle popup sweetalert se aata tha, jo jQuery + do extra files
 * maangta tha. Ab wahi kaam neeche wale chhote <Dialog> se hota hai, isliye
 * page par ab koi legacy script load nahi hoti.
 *
 * Form successfully submit hone par dialog nahi, neeche wala <Toast> aata hai —
 * user ko OK dabana nahi padta, message khud gayab ho jaata hai.
 */

const VALID_EXT = ['txt', 'doc', 'docx', 'pdf', 'jpg', 'jpeg', 'png']

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const PHONE_RE =
  /^((\+)?[1-9]{1,2})?([-\s.])?((\(\d{1,4}\))|\d{1,4})(([-\s.])?[0-9]{1,12}){1,2}$/

type DialogState = {
  tone: 'success' | 'error'
  title: string
  message: string
} | null

type Upload_ = 'idle' | 'uploading' | 'done' | 'failed'

export default function InternshipForm() {
  const [fname, setFname] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [currInst, setCurrInst] = useState('')
  const [currYear, setCurrYear] = useState('')
  const [duration, setDuration] = useState('')
  const [resumeLink, setResumeLink] = useState('')
  const [fileName, setFileName] = useState('')

  const [uploadState, setUploadState] = useState<Upload_>('idle')
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState<DialogState>(null)
  const [toast, setToast] = useState('')
  const [saving, setSaving] = useState(false)

  const fnameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const instRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLSelectElement>(null)
  const durationRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  /* .txtOnly — Internship.js wala keypress filter, jaisa ka taisa */
  const txtOnly = (e: React.KeyboardEvent) => {
    const key = e.which || e.keyCode
    if (key >= 33 && key <= 64) e.preventDefault()
    if (key >= 123 && key <= 127) e.preventDefault()
    if (key >= 91 && key <= 96) e.preventDefault()
  }

  /* .numOnly */
  const numOnly = (e: React.KeyboardEvent) => {
    const key = e.which || e.keyCode
    if (key >= 58 && key <= 127) e.preventDefault()
    if (key >= 1 && key <= 47) e.preventDefault()
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    const dot = file.name.lastIndexOf('.') + 1
    const ext = file.name.substring(dot).toLowerCase()
    if (dot === 0 || !VALID_EXT.includes(ext)) {
      setUploadState('failed')
      setFileName('')
      setDialog({
        tone: 'error',
        title: 'Oops!',
        message:
          'Please Upload file with extension .doc, .docx, .txt, .pdf, .jpg, jpeg, .png !',
      })
      return
    }

    setFileName(file.name)
    setUploadState('uploading')

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await fetch('/api/upload', { method: 'POST', body: formData })
      const status = await res.json()
      if (status.isSuccess && typeof status.record === 'string' && status.record) {
        setResumeLink(status.record)
        setUploadState('done')
        setDialog({
          tone: 'success',
          title: 'Uploaded',
          message: 'Resume Uploaded Successfully',
        })
      } else {
        setUploadState('failed')
        setDialog({ tone: 'error', title: 'Oops!', message: 'Invalid File' })
      }
    } catch {
      setUploadState('failed')
      setDialog({ tone: 'error', title: 'Oops!', message: 'Something wrong!' })
    }
  }

  function clearAll() {
    setFname('')
    setPhone('')
    setEmail('')
    setCurrInst('')
    setCurrYear('')
    setDuration('')
    setResumeLink('')
    setFileName('')
    setUploadState('idle')
    if (fileRef.current) fileRef.current.value = ''
  }

  function fail(message: string, ref?: React.RefObject<HTMLElement | null>) {
    setError(message)
    ref?.current?.focus()
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    /* Validation ka order aur text purane Internship.js jaisa hi hai. */
    if (fname === '') return fail('Please  Enter Your Name !', fnameRef)
    if (phone === '') return fail('Please Enter Your Phone Number !', phoneRef)
    if (!(phone.length === 10 && PHONE_RE.test(phone)))
      return fail('Please Enter Your 10 Digit Mobile Number !', phoneRef)
    if (email === '') return fail('Please Enter Your E-Mail !', emailRef)
    if (!EMAIL_RE.test(email)) return fail('Please Enter Valid E-Mail Id !', emailRef)
    if (currInst === '') return fail('Please Enter Name of Current Institute !', instRef)
    if (currYear === '') return fail('Please Select Current Year !', yearRef)
    if (duration === '') return fail('Please Enter Duration of Internship !', durationRef)
    if (resumeLink === '') return fail('Upload Your Resume !')

    setSaving(true)
    try {
      const res = await fetch('/api/internship', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Name: fname,
          Phone: phone,
          Email: email,
          Curr_Ins_Name: currInst,
          Curr_Year: currYear,
          Duration: duration,
          Resume: resumeLink,
        }),
      })
      const data = await res.json()
      if (data.IsSuccess) {
        setToast('Our team will get back to you on the contact details you provide.')
        clearAll()
      } else {
        /*
         * Server ka asli message dikhate hain (validation fail hone par wahi
         * kaam ka hota hai); sirf fallback me generic text.
         */
        setDialog({
          tone: 'error',
          title: 'oops!!',
          message: data.Message || 'Something was Wrong',
        })
      }
    } catch {
      setDialog({ tone: 'error', title: 'oops!!', message: 'Something was Wrong' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <form className="form-panel" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="txtFname">
            Full Name <span className="req">*</span>
          </label>
          <input
            ref={fnameRef}
            id="txtFname"
            className="input"
            type="text"
            maxLength={40}
            placeholder="Your full name"
            style={{ textTransform: 'capitalize' }}
            value={fname}
            onKeyPress={txtOnly}
            onChange={(e) => setFname(e.target.value)}
          />
        </div>

        <div className="grid grid-2" style={{ gap: 18 }}>
          <div className="field">
            <label className="field__label" htmlFor="txtPhone">
              Phone <span className="req">*</span>
            </label>
            <input
              ref={phoneRef}
              id="txtPhone"
              className="input"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="10 digit mobile number"
              value={phone}
              onKeyPress={numOnly}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="txtEmail">
              E-mail <span className="req">*</span>
            </label>
            <input
              ref={emailRef}
              id="txtEmail"
              className="input"
              type="email"
              maxLength={40}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="txtCurrInt">
            Current Institute Name <span className="req">*</span>
          </label>
          <input
            ref={instRef}
            id="txtCurrInt"
            className="input"
            type="text"
            maxLength={40}
            placeholder="Name of your college / university"
            style={{ textTransform: 'capitalize' }}
            value={currInst}
            onKeyPress={txtOnly}
            onChange={(e) => setCurrInst(e.target.value)}
          />
        </div>

        {/* Year, duration aur resume — teeno ek hi line me */}
        <div className="grid grid-3 row--resume" style={{ gap: 18 }}>
          <div className="field">
            <label className="field__label" htmlFor="selCurrYear">
              Current Year Studying In <span className="req">*</span>
            </label>
            <select
              ref={yearRef}
              id="selCurrYear"
              className="select"
              value={currYear}
              onChange={(e) => setCurrYear(e.target.value)}
            >
              {/* Column ab sankra hai, isliye placeholder chhota — label upar hai hi */}
              <option value="">--Select--</option>
              <option value="First">1st</option>
              <option value="Second">2nd</option>
              <option value="Third">3rd</option>
              <option value="Fourth">4th</option>
              <option value="Fifth">5th</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="txtDurrInt">
              Duration of Internship <span className="req">*</span>
            </label>
            <input
              ref={durationRef}
              id="txtDurrInt"
              className="input"
              type="text"
              maxLength={40}
              placeholder="e.g. 4 weeks"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>

          <div className="field">
            <span className="field__label field__label--with-hint">
              <span>
                Resume <span className="req">*</span>
              </span>
              {/* Formats label ke bagal me, chhote font me — button ke neeche jagah bachti hai */}
              <span className="field__formats">(PDF, DOC, DOCX, TXT, JPG, JPEG, PNG)</span>
            </span>
            <div className="file-row">
              <label className="file-btn file-btn--block" htmlFor="pro_img_r">
                <Upload />
                {uploadState === 'uploading' ? 'Uploading…' : 'Upload Resume'}
              </label>
              <input
                ref={fileRef}
                id="pro_img_r"
                type="file"
                className="sr-only"
                accept=".txt,.doc,.docx,.pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              {fileName && uploadState === 'done' && (
                <span className="file-name">✓ {fileName}</span>
              )}
              {fileName && uploadState === 'uploading' && (
                <span className="file-name">{fileName}</span>
              )}
            </div>
          </div>
        </div>

        {error && (
          <p className="field__error" role="alert" style={{ marginBottom: 16 }}>
            {error}
          </p>
        )}

        <button type="submit" className="btn btn--navy" disabled={saving}>
          {saving ? 'Please Wait !!!' : 'Submit Now'}
        </button>
      </form>

      {toast && <Toast message={toast} onClose={() => setToast('')} />}

      {dialog && (
        <Dialog
          tone={dialog.tone}
          title={dialog.title}
          message={dialog.message}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  )
}

/*
 * Submit hone ke baad ka confirmation. Dialog se halka hai: screen ke kone me
 * aata hai, kuch second baad khud chala jaata hai, aur form ko block nahi karta.
 */
function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000)
    return () => clearTimeout(t)
  }, [onClose])

  return (
    <div className="toast" role="status" aria-live="polite">
      <span className="toast__icon">
        <CheckCircle size={20} />
      </span>
      <p className="toast__msg">{message}</p>
      <button type="button" className="toast__close" onClick={onClose} aria-label="Close">
        <Close size={16} />
      </button>
    </div>
  )
}

/* sweetalert ka chhota, self-contained replacement */
function Dialog({
  tone,
  title,
  message,
  onClose,
}: {
  tone: 'success' | 'error'
  title: string
  message: string
  onClose: () => void
}) {
  return (
    <div
      className="dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      {/* andar click karne par band na ho */}
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <span className={`dialog__icon dialog__icon--${tone}`}>
          {tone === 'success' ? <CheckCircle /> : <AlertCircle />}
        </span>
        <h3 className="dialog__title">{title}</h3>
        <p className="dialog__msg">{message}</p>
        <div className="dialog__actions">
          <button type="button" className="btn btn--navy" onClick={onClose} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  )
}
